import { useEffect, useRef, useState } from "react";
import { getPresignedUrls } from "../../utils/utilityFunctions";

/**
 * Image with a resilient loading chain for the private fmv-data bucket:
 *   1. `src`        — the CDN (Thumbor) thumbnail (fast, compressed).
 *   2. on error     — a short-lived presigned URL of the original (`fallbackSrc`).
 *
 * The CDN can't serve every object (e.g. ".jfif", which 400s), and the raw S3
 * URL 403s on a private bucket — so presign is the reliable fallback. The
 * presign request only fires if the CDN image actually fails to load.
 */
function SecureImg({ src, fallbackSrc, alt = "", ...rest }) {
  const [currentSrc, setCurrentSrc] = useState(src);
  const triedPresign = useRef(false);

  // Reset when the CDN src changes (e.g. carousel slide / re-render with new image)
  useEffect(() => {
    setCurrentSrc(src);
    triedPresign.current = false;
  }, [src]);

  const handleError = async () => {
    if (triedPresign.current || !fallbackSrc) return;
    triedPresign.current = true;
    try {
      const [signed] = await getPresignedUrls([fallbackSrc]);
      setCurrentSrc(signed || fallbackSrc);
    } catch {
      setCurrentSrc(fallbackSrc);
    }
  };

  return <img src={currentSrc} alt={alt} onError={handleError} {...rest} />;
}

export default SecureImg;
